<template>
  <div class="forgot-password-container">
    <div class="forgot-form-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <div class="logo-wrapper">
          <img src="@/assets/logo.png" alt="QUALI-D" class="logo" />
        </div>
        <p class="brand-subtitle">Measure, Improve, Succeed</p>
      </div>

      <!-- Back to Login Link -->
      <div class="back-link-section">
        <router-link to="/login" class="back-link">
          ← Back to Login
        </router-link>
      </div>

      <!-- Step 1: Phone Number Input -->
      <div v-if="step === 1" class="form-section">
        <div class="form-header">
          <h2 class="form-title">Reset Password</h2>
          <p class="form-subtitle">Enter your phone number to receive a reset code</p>
        </div>

        <form @submit.prevent="requestPasswordReset" class="forgot-form">
          <div class="form-group">
            <label for="phone" class="form-label">Phone Number</label>
            <div class="phone-input-group">
              <CountryCodeSelector
                v-model="countryCode"
                :has-error="!!errors.countryCode"
              />
              <div class="input-wrapper phone-input-wrapper">
                <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                <input
                  id="phone"
                  v-model="phoneNumber"
                  type="tel"
                  placeholder="Enter phone number"
                  class="form-input"
                  :class="{ 'error': errors.phone }"
                  required
                />
              </div>
            </div>
            <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
            <span v-if="errors.countryCode" class="error-message">{{ errors.countryCode }}</span>
          </div>

          <button
            type="submit"
            class="submit-button"
            :disabled="isLoading"
            :class="{ 'loading': isLoading }"
          >
            <span v-if="!isLoading">Send Reset Code</span>
            <span v-else class="loading-spinner">
              <span class="spinner"></span>
              Sending...
            </span>
          </button>

          <div v-if="errorMessage" class="error-alert">
            {{ errorMessage }}
          </div>
        </form>
      </div>

      <!-- Step 2: OTP Verification and New Password -->
      <div v-if="step === 2" class="form-section">
        <div class="form-header">
          <h2 class="form-title">Enter Reset Code</h2>
          <p class="form-subtitle">
            We sent a 6-digit code to your WhatsApp
            <br>
            <strong>+{{ displayCountryCode }} {{ phoneNumber }}</strong>
          </p>
        </div>

        <form @submit.prevent="resetPassword" class="forgot-form">
          <div class="form-group">
            <label for="otp" class="form-label">6-Digit Code</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              <input
                id="otp"
                v-model="otpCode"
                type="text"
                placeholder="Enter 6-digit code"
                class="form-input"
                :class="{ 'error': errors.otp }"
                maxlength="6"
                required
              />
            </div>
            <span v-if="errors.otp" class="error-message">{{ errors.otp }}</span>
          </div>

          <div class="form-group">
            <label for="newPassword" class="form-label">New Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="newPassword"
                v-model="newPassword"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter new password"
                class="form-input"
                :class="{ 'error': errors.newPassword }"
                required
              />
              <button
                type="button"
                @click="togglePassword"
                class="toggle-password"
              >
                <svg v-if="!showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.newPassword" class="error-message">{{ errors.newPassword }}</span>
          </div>

          <div class="form-group">
            <label for="confirmPassword" class="form-label">Confirm Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirm new password"
                class="form-input"
                :class="{ 'error': errors.confirmPassword }"
                required
              />
              <button
                type="button"
                @click="toggleConfirmPassword"
                class="toggle-password"
              >
                <svg v-if="!showConfirmPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</span>
          </div>

          <div class="form-actions">
            <button
              type="button"
              @click="goBackToStep1"
              class="secondary-button"
              :disabled="isLoading"
            >
              Use Different Phone
            </button>
            <button
              type="submit"
              class="submit-button"
              :disabled="isLoading"
              :class="{ 'loading': isLoading }"
            >
              <span v-if="!isLoading">Reset Password</span>
              <span v-else class="loading-spinner">
                <span class="spinner"></span>
                Resetting...
              </span>
            </button>
          </div>

          <div v-if="errorMessage" class="error-alert">
            {{ errorMessage }}
          </div>
        </form>
      </div>

      <!-- Step 3: Success Message -->
      <div v-if="step === 3" class="form-section">
        <div class="success-section">
          <div class="success-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
          </div>
          <h2 class="success-title">Password Reset Successfully!</h2>
          <p class="success-message">
            Your password has been reset successfully. You are now being logged in...
          </p>
          <div class="loading-spinner">
            <span class="spinner"></span>
            Logging you in...
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { AuthService } from '@/services/auth'
import { authStore } from '@/store/auth'
import { DEFAULT_COUNTRY_CODE } from '@/utils/countryCodes'
import CountryCodeSelector from '@/components/CountryCodeSelector.vue'

export default {
  name: 'ForgotPasswordView',
  components: {
    CountryCodeSelector
  },
  data () {
    return {
      step: 1,
      countryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber: '',
      otpCode: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      errors: {},
      errorMessage: ''
    }
  },
  computed: {
    displayCountryCode () {
      // Special case for Egypt: display "+2" instead of "+20"
      return this.countryCode === '20' ? '2' : this.countryCode
    }
  },
  methods: {
    togglePassword () {
      this.showPassword = !this.showPassword
    },
    toggleConfirmPassword () {
      this.showConfirmPassword = !this.showConfirmPassword
    },
    validatePhoneNumber () {
      this.errors = {}
      let isValid = true

      if (!this.phoneNumber.trim()) {
        this.errors.phone = 'Phone number is required'
        isValid = false
      } else if (!/^[0-9+\-\s()]+$/.test(this.phoneNumber)) {
        this.errors.phone = 'Please enter a valid phone number'
        isValid = false
      }

      return isValid
    },
    validateResetForm () {
      this.errors = {}
      let isValid = true

      if (!this.otpCode.trim()) {
        this.errors.otp = 'Reset code is required'
        isValid = false
      } else if (!/^\d{6}$/.test(this.otpCode)) {
        this.errors.otp = 'Please enter a valid 6-digit code'
        isValid = false
      }

      if (!this.newPassword.trim()) {
        this.errors.newPassword = 'New password is required'
        isValid = false
      } else if (this.newPassword.length < 6) {
        this.errors.newPassword = 'Password must be at least 6 characters'
        isValid = false
      }

      if (!this.confirmPassword.trim()) {
        this.errors.confirmPassword = 'Please confirm your password'
        isValid = false
      } else if (this.newPassword !== this.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match'
        isValid = false
      }

      return isValid
    },
    async requestPasswordReset () {
      this.errorMessage = ''

      if (!this.validatePhoneNumber()) {
        return
      }

      this.isLoading = true

      try {
        // Special case for Egypt: use "+2" instead of "+20"
        const countryCode = this.countryCode === '20' ? '2' : this.countryCode

        const response = await fetch('https://api.quali-d.com/password/reset-request', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          mode: 'cors',
          body: JSON.stringify({
            country_code: countryCode,
            phone: this.phoneNumber
          })
        })

        const data = await response.json()

        if (!response.ok || (data.success === false)) {
          throw new Error(data.message || 'Failed to send reset code')
        }

        console.log('Password reset request successful:', data)
        this.step = 2
      } catch (error) {
        console.error('Password reset request failed:', error)
        this.errorMessage = error.message || 'Failed to send reset code. Please try again.'
      } finally {
        this.isLoading = false
      }
    },
    async resetPassword () {
      this.errorMessage = ''

      if (!this.validateResetForm()) {
        return
      }

      this.isLoading = true

      try {
        // Special case for Egypt: use "+2" instead of "+20"
        const countryCode = this.countryCode === '20' ? '2' : this.countryCode

        // Step 1: Reset password with OTP
        const resetResponse = await fetch('https://api.quali-d.com/password/reset', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          mode: 'cors',
          body: JSON.stringify({
            country_code: countryCode,
            phone: this.phoneNumber,
            token: this.otpCode,
            new_password: this.newPassword,
            confirm_password: this.confirmPassword
          })
        })

        const resetData = await resetResponse.json()

        if (!resetResponse.ok || (resetData.success === false)) {
          throw new Error(resetData.message || 'Failed to reset password')
        }

        console.log('Password reset successful:', resetData)
        this.step = 3

        // Step 2: Automatically login with new credentials
        setTimeout(async () => {
          try {
            const loginResponse = await authStore.login({
              country_code: countryCode,
              phone: this.phoneNumber,
              password: this.newPassword
            })

            console.log('Auto-login successful:', loginResponse)

            if (AuthService.isAuthenticated()) {
              this.$router.push('/')
            } else {
              // Fallback to login page if auto-login fails
              this.$router.push('/login?message=Password reset successful. Please login with your new password.')
            }
          } catch (loginError) {
            console.error('Auto-login failed:', loginError)
            // Redirect to login page with success message
            this.$router.push('/login?message=Password reset successful. Please login with your new password.')
          }
        }, 2000)
      } catch (error) {
        console.error('Password reset failed:', error)
        this.errorMessage = error.message || 'Failed to reset password. Please try again.'
      } finally {
        this.isLoading = false
      }
    },
    goBackToStep1 () {
      this.step = 1
      this.otpCode = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.errors = {}
      this.errorMessage = ''
    }
  }
}
</script>

<style scoped>
.forgot-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  position: relative;
  overflow: hidden;
}

.forgot-form-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 480px;
  margin: 20px;
  position: relative;
  z-index: 10;
}

.logo-section {
  text-align: center;
  margin-bottom: 32px;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.logo {
  height: 48px;
  width: auto;
}

.brand-subtitle {
  color: #6b7280;
  font-size: 16px;
  margin: 0;
  font-weight: 500;
}

.back-link-section {
  margin-bottom: 24px;
}

.back-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.back-link:hover {
  text-decoration: underline;
}

.form-section {
  width: 100%;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
}

.form-subtitle {
  color: #6b7280;
  font-size: 16px;
  margin: 0;
  line-height: 1.5;
}

.forgot-form {
  width: 100%;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  opacity: 0.6;
  z-index: 2;
  color: #6b7280;
}

.form-input {
  width: 100%;
  padding: 16px 16px 16px 52px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  background: white;
  color: #1f2937;
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.toggle-password {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  opacity: 1;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 14px;
  margin-top: 6px;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.submit-button {
  flex: 1;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.secondary-button {
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.secondary-button:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #d1d5db;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.submit-button:disabled,
.secondary-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
}

.success-section {
  text-align: center;
  padding: 32px 0;
}

.success-icon {
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.success-title {
  font-size: 24px;
  font-weight: 700;
  color: #059669;
  margin: 0 0 16px;
}

.success-message {
  color: #6b7280;
  font-size: 16px;
  margin: 0 0 32px;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .forgot-form-container {
    padding: 32px 24px;
    margin: 16px;
  }

  .form-title {
    font-size: 24px;
  }

  .form-actions {
    flex-direction: column;
  }
}

/* Phone Input Group Styles */
.phone-input-group {
  display: flex;
  gap: 8px;
  width: 100%;
}

.country-selector-wrapper {
  flex-shrink: 0;
}

.country-select {
  width: 120px;
  padding: 16px 8px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  background: white;
  color: #1f2937;
  transition: all 0.2s;
  outline: none;
  cursor: pointer;
  height: 56px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Apple Color Emoji", Arial, sans-serif;
}

.country-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.country-select.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.phone-input-wrapper {
  flex: 1;
}

.phone-input-wrapper .form-input {
  width: 100%;
}

@media (max-width: 480px) {
  .country-select {
    width: 100px;
    font-size: 13px;
  }
}
</style>
